/**
 * Image Optimization Utilities
 * Provides helper functions to use optimized image versions
 */

import { resolveMediaUrl } from './mediaUrl';

/**
 * Get the optimized image URL based on context and browser support
 * @param {Object} image - Image object from product/media
 * @param {string} size - 'thumbnail', 'medium', or 'large'
 * @param {boolean} preferWebP - Whether to prefer WebP format (default: auto-detect)
 * @returns {string} - Optimized image URL
 */
export const getOptimizedImageUrl = (image, size = 'medium', preferWebP = null) => {
  // Auto-detect WebP support if not specified
  if (preferWebP === null) {
    preferWebP = supportsWebP();
  }

  // If image has optimized versions, use them
  if (image?.optimized && image.optimized[size]) {
    const format = preferWebP ? 'webp' : 'jpg';
    return resolveMediaUrl(image.optimized[size]?.[format]?.url || image[size] || image.url);
  }

  // Fallback to original structure
  return resolveMediaUrl(image?.[size] || image?.url || image?.original || '');
};

/**
 * Get image URL with fallback chain
 * Tries: optimized -> size-specific -> original -> fallback
 */
export const getImageUrl = (image, size = 'medium', fallback = '/placeholder.jpg') => {
  if (!image) return fallback;

  // Try optimized version first
  const optimized = getOptimizedImageUrl(image, size);
  if (optimized) return optimized;

  // Fallback chain
  return resolveMediaUrl(image[size] || image.url || image.original) || fallback;
};

/**
 * Get responsive image srcset for better performance.
 *
 * Supports both image shapes used across the codebase:
 *   1. Modern: `image.optimized.<size>.<format>.url`
 *   2. Flat (current product API): `image.thumbnail`, `image.medium`,
 *      `image.original` / `image.large` (all URLs already on the CDN).
 *
 * @param {Object} image
 * @returns {string} srcset string for the <img> tag
 */
export const getImageSrcSet = (image) => {
  if (!image) return '';
  const srcset = [];

  // Shape 1 — explicit optimized variants (preferred, supports WebP)
  if (image.optimized) {
    const format = supportsWebP() ? 'webp' : 'jpg';
    if (image.optimized.thumbnail?.[format]?.url) {
      srcset.push(`${resolveMediaUrl(image.optimized.thumbnail[format].url)} 300w`);
    }
    if (image.optimized.medium?.[format]?.url) {
      srcset.push(`${resolveMediaUrl(image.optimized.medium[format].url)} 800w`);
    }
    if (image.optimized.large?.[format]?.url) {
      srcset.push(`${resolveMediaUrl(image.optimized.large[format].url)} 1400w`);
    }
    if (srcset.length) return srcset.join(', ');
  }

  // Shape 2 — flat URL fields (handles _thumbnail/_medium/_large suffixes
  // emitted by the backend optimizer).
  if (image.thumbnail) srcset.push(`${resolveMediaUrl(image.thumbnail)} 300w`);
  if (image.medium)    srcset.push(`${resolveMediaUrl(image.medium)} 800w`);
  const large = image.large || image.original;
  if (large)            srcset.push(`${resolveMediaUrl(large)} 1400w`);

  return srcset.join(', ');
};

/**
 * Default `sizes` attribute for product cards in the storefront grids.
 * Matches the shop layout (3-col desktop / 2-col tablet / 1-col mobile).
 */
export const PRODUCT_CARD_SIZES =
  '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw';

/**
 * Check if browser supports WebP
 * Uses cached result for performance
 */
let webpSupport = null;

export const supportsWebP = () => {
  if (webpSupport !== null) return webpSupport;

  // Create a test WebP image
  const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
  const img = new Image();
  img.src = webpData;

  webpSupport = img.width > 0 && img.height > 0;
  return webpSupport;
};

/**
 * Get SEO-optimized image attributes
 * Returns alt, title, and other SEO attributes
 */
export const getImageSEO = (product, imageIndex = 0) => {
  const image = product.images?.[imageIndex];
  const productName = typeof product.name === 'string' ? product.name : product.name?.en || product.name?.es || 'Product';
  
  // Handle multilingual alt text
  let altText = productName;
  if (image?.alt) {
    if (typeof image.alt === 'string') {
      altText = image.alt;
    } else if (typeof image.alt === 'object') {
      // Multilingual alt - try to get based on current language or fallback
      const lang = document.documentElement.lang || 'en';
      altText = image.alt[lang] || image.alt.en || image.alt.es || productName;
    }
  }
  
  return {
    alt: altText || `${productName} - Image ${imageIndex + 1}`,
    title: productName,
    loading: imageIndex === 0 ? 'eager' : 'lazy', // Load first image immediately, others lazy
  };
};

/**
 * Preload critical images for better performance
 */
export const preloadImage = (url) => {
  if (!url) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  document.head.appendChild(link);
};

/**
 * Get appropriate image size based on viewport
 */
export const getImageSizeForViewport = () => {
  const width = window.innerWidth;
  
  if (width < 640) return 'thumbnail';  // Mobile
  if (width < 1024) return 'medium';     // Tablet
  return 'large';                        // Desktop
};
